<template>
  <base-wrapper :loading="loading">
    <div class="space-y-4">
      <base-card title="Import Stock">
        <form class="space-y-4" @submit.prevent="onProcess">
          <base-input
            label="Pilih File"
            fullwidth
            :shadow="false"
            type="file"
            accept=".csv"
            size="padding-md"
            required
            @change="onChangeFile"
          />
          <base-button type="submit" :loading="loadingProcess.visible">{{
            loadingProcess.visible ? loadingProcessLabel : 'Proses'
          }}</base-button>
        </form>
      </base-card>

      <base-card title="Keterangan">
        <dl class="grid gap-4">
          <div class="w-full">
            <dt class="text-sm text-gray-900">
              Contoh file import stock (.csv)
            </dt>
            <dd class="mt-1">
              <a
                href="/import-stock-example/import_stock_kode_office_ddmmyyyy_hhmm.csv"
                class="text-sm font-medium text-green-600"
                >import_stock_kode_office_ddmmyyyy_hhmm.csv</a
              >
            </dd>
          </div>
        </dl>
      </base-card>

      <Datatable
        :total="histories.meta.page.total"
        :perPage="histories.meta.page.perPage"
        :currentPage="histories.meta.page.currentPage"
        :columns="[
          { id: 'date', name: 'Tanggal' },
          { id: 'user', name: 'User' },
          { id: 'status', name: 'Status', theadClass: 'text-center' },
          { id: 'download', name: 'Download File', theadClass: 'text-center' },
          { id: 'download_error', name: 'Download Error', theadClass: 'text-center' },
        ]"
        @pagechanged="onChangeHistoryPage"
      >
        <template #tbody="{ classes }">
          <tr
            v-for="history in histories.data"
            :key="history.id"
            :class="[classes.tr, 'bg-white']"
          >
            <td :class="classes.td">
              {{ history.attributes.createdAt | formatDate }}
            </td>
            <td :class="classes.td">{{ history.attributes.user_email }}</td>
            <td :class="[classes.td, 'text-center']">
              <BaseBadge
                v-if="history.attributes.status === 'queued'"
                color="yellow"
                >Menunggu Diproses</BaseBadge
              >
              <BaseBadge
                v-else-if="history.attributes.status === 'processing'"
                color="blue"
                >Sedang Diproses</BaseBadge
              >
              <BaseBadge
                v-else-if="
                  history.attributes.status === 'finished' &&
                  history.attributes.failed_file_url
                "
                color="red"
                >Selesai dengan Error</BaseBadge
              >
              <BaseBadge v-else color="green">Selesai</BaseBadge>
            </td>
            <td :class="[classes.td, 'text-center']">
              <a
                v-if="history.attributes.file_url"
                :href="history.attributes.file_url"
                class="flex justify-center text-green-600"
              >
                <Icon icon="heroicons:arrow-down-tray" class="h-4 w-4" />
              </a>
            </td>
            <td :class="[classes.td, 'text-center']">
              <a
                v-if="history.attributes.failed_file_url"
                :href="history.attributes.failed_file_url"
                class="flex justify-center text-green-600"
              >
                <Icon icon="heroicons:arrow-down-tray" class="h-4 w-4" />
              </a>
            </td>
          </tr>
        </template>
      </Datatable>
    </div>
  </base-wrapper>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import { mapGetters, mapActions } from 'vuex';

export default {
  mixins: [requestMixin],
  data() {
    return {
      file: null,
      loadingHistories: false,
      loadingProcess: {
        visible: false,
        text: null,
      },
      histories: {
        data: [],
        meta: {
          page: {},
        },
      },
      chunk: {
        total: 0,
        completed: 0
      }
    };
  },
  computed: {
    ...mapGetters({
      me: 'auth/getUser',
      getChannelName: 'auth/getChannelName',
    }),
    loading() {
      return this.loadingHistories;
    },
    loadingProcessLabel() {
      if (this.loadingProcess.text === 'processing') {
        return `Memproses ${Math.floor(this.chunk.completed/this.chunk.total * 100)}%`
      }

      return {
        wait: 'Mengirim Data',
        queued: 'Menunggu Diproses',
        processing: 'Sedang Diproses',
      }[this.loadingProcess.text];
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    addListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      userChannel.bind(
        'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
        this.onProgress
      );
    },
    async loadImportHistories(params) {
      this.loadingHistories = true;

      const [res, err] = await this.request(
        '/api/v1/import-stock-batch-histories',
        {
          params: {
            'page[size]': 5,
            sort: '-id',
            ...params,
          },
        }
      );

      if (!err) {
        const running = res.data.some(
          (history) => history.attributes.status !== 'finished'
        );

        if (running) {
          const history = res.data.find(history => history.attributes.status !== 'finished')

          this.chunk.completed = history.attributes.completed_chunk 
          this.chunk.total = history.attributes.total_chunk

          this.startLoadingProcess(res.data[0].attributes.status);
        }

        this.histories = res;
      }

      this.loadingHistories = false;
    },
    onProgress(e) {
      if (e.type === 'App\\Notifications\\ImportStockBatchProcessUpdated') {
        if (e.status === 'processing') {
          this.startLoadingProcess('processing');
        } else if (e.status === 'chunking') {
          this.chunk.completed = e.completed_chunk
        } else if (e.status === 'failed') {
          this.stopLoadingProcess();

          this.createAlert({
            status: 'error',
            data: e.message,
          });
        } else if (e.status === 'finished') {
          this.stopLoadingProcess();

          this.createAlert({
            status: 'success',
            data: 'Berhasil mengimport stock',
          });

          this.loadImportHistories();
        }
      }
    },
    onChangeFile(e) {
      this.file = e.target.files[0];
    },
    onChangeHistoryPage(page) {
      this.loadImportHistories({
        'page[number]': page,
      });
    },
    async onProcess() {
      this.startLoadingProcess('wait');

      const formData = new FormData();

      formData.append('file', this.file);

      const [res, err] = await this.request(
        '/api/v1/stocks/-actions/import-batch',
        {
          method: 'post',
          data: formData,
        }
      );

      if (!err) {
        this.startLoadingProcess('queued');

        this.chunk.total = res.total_chunk;
        this.chunk.completed = 0;
      } else {
        this.stopLoadingProcess();
      }
    },
    removeListener() {
      const userChannel = this.$pusher.channel(this.getChannelName);

      if (userChannel) {
        userChannel.unbind(
          'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
          this.onProgress
        );
      }
    },
    startLoadingProcess(text) {
      this.loadingProcess.text = text;
      this.loadingProcess.visible = true;
    },
    stopLoadingProcess() {
      this.loadingProcess.visible = false;
    },
  },
  created() {
    this.loadImportHistories();
    this.addListener();
  },
  beforeRouteLeave(to, from, next) {
    this.removeListener();
    next();
  },
};
</script>
